import { useCallback, useEffect, useRef } from 'react';

export type LongPressOptions = {
  /** Fires as soon as the press begins (first mousedown / touchstart). */
  onPressStart?: () => void;
  /** Fires when the press ends (mouseup, touch end, leave, or cancel). */
  onPressEnd?: () => void;
};

/** Accelerating press-and-hold — used for stake / autoplay +/- buttons. */
export function useLongPress(
  callback: () => void,
  initialSpeed = 200,
  acceleration = 0.8,
  delay = 300,
  options?: LongPressOptions
) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const speedRef = useRef(initialSpeed);
  const activeRef = useRef(false);
  const targetRef = useRef<EventTarget | null>(null);
  const touchHandled = useRef(false);

  const repeat = useCallback(() => {
    if (!activeRef.current) return;
    callback();
    speedRef.current = Math.max(50, speedRef.current * acceleration);
    timeoutRef.current = setTimeout(repeat, speedRef.current);
  }, [callback, acceleration]);

  const handlePressStart = useCallback(
    (target: EventTarget) => {
      if (activeRef.current) {
        return;
      }

      activeRef.current = true;
      targetRef.current = target;
      options?.onPressStart?.();
      callback();
      speedRef.current = initialSpeed;
      timeoutRef.current = setTimeout(() => {
        if (!activeRef.current) {
          return;
        }
        repeat();
      }, delay);
    },
    [callback, delay, initialSpeed, options, repeat]
  );

  const stop = useCallback(() => {
    if (activeRef.current) {
      options?.onPressEnd?.();
    }
    activeRef.current = false;
    targetRef.current = null;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [options]);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!targetRef.current) return;
      const touch = e.touches[0];
      const elementUnderTouch = document.elementFromPoint(
        touch.clientX,
        touch.clientY
      );
      if (
        targetRef.current instanceof Element &&
        !targetRef.current.contains(elementUnderTouch)
      ) {
        stop();
      }
    },
    [stop]
  );

  useEffect(() => () => stop(), [stop]);

  return {
    onMouseDown: (e: React.MouseEvent) => {
      if (touchHandled.current) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      handlePressStart(e.currentTarget);
    },
    onMouseUp: stop,
    onMouseLeave: stop,
    onTouchStart: (e: React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      touchHandled.current = true;
      setTimeout(() => {
        touchHandled.current = false;
      }, 600);
      handlePressStart(e.currentTarget);
    },
    onTouchEnd: stop,
    onTouchMove: handleTouchMove,
    onTouchCancel: stop,
  };
}
